import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Image,
  Link,
  Spinner,
  Text as ChakraText,
  Tooltip,
  useToken,
  VStack
} from '@chakra-ui/react'
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useRouteMatch } from 'react-router-dom'
import type { Interceptors } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import ExternalStorageGraphic from '../../../graphics/ExternalStorageGraphic'
import { colors } from '../../../ui-kit'
import {
  extractMetadata,
  getOriginIconUrl,
  sourceTypeEnum,
  sourceTypeMapping
} from '../../../utils/source'
import type { SourcesResponse } from '../../interfaces'
import { SideBar } from '../../layouts/Base'
import { SideBarItem } from '../../layouts/Base/SideBar'
import { UserContext } from '../../providers'
import SearchBar from '../SearchBar'
import Shadow from '../Shadow'
import PermissionGuard from '../PermissionGuard'
import { encodeHash, PermissionsEnum } from '../../../utils'

const ExternalStorageSideMenu = forwardRef<ExternalStorageSideMenuRef, unknown>(
  (_props, ref) => {
    const { t } = useTranslation()

    const { path } = useRouteMatch()

    const [searchQuery, setSearchQuery] = useState<string>('')

    const { user } = useContext(UserContext)

    const interceptors: Interceptors<SourcesResponse[]> = {
      response: async ({ response }) => {
        if (response.data) {
          response.data = response.data.filter(x => x.isDefault !== true)
        }
        return response
      },
    }

    const { get, data, loading } = useFetch<SourcesResponse[]>({
      interceptors,
      cachePolicy: CachePolicies.NO_CACHE,
    })

    const getSources = async (): Promise<void> => {
      if (user?.clientNumber) {
        const resp = await get(`/api/clients/${user.clientNumber}/sources`)
        if (resp) {
          window.clientDataStore = {
            ...window.clientDataStore,
            sourceLength: resp.length,
          }
          window.dispatchEvent(new CustomEvent("sourceLengthUpdated"));
        }
      }
    }

    useEffect(() => {
      void getSources()
    }, [user])

    useImperativeHandle(ref, () => ({
      getSources,
    }))

    const backgroundColor = useToken('colors', ['appPaleGrey']) as string

    if (user === null) {
      return null
    }

    const filteredData = data
      ? data
          .map(x => {
            if (x.typeId === sourceTypeEnum.WEB_FOLDER && x.baseUrl === '') {
              x.typeId = sourceTypeEnum.WEB_PROXY
            }

            if (x.typeId === sourceTypeEnum.AMAZON_S3 && x.endpoint) {
              x.typeId = sourceTypeEnum.S3_COMPATIBLE_STORAGE
            }
            return x
          })
          .filter(x =>
            searchQuery
              ? x.name.toUpperCase().includes(searchQuery.toUpperCase()) ||
                sourceTypeMapping[x.typeId].name
                  .toUpperCase()
                  .includes(searchQuery.toUpperCase()) ||
                extractMetadata(x)
                  .sourceDescription.toUpperCase()
                  .includes(searchQuery.toUpperCase())
              : true,
          )
      : []

    return (
      <PermissionGuard
        permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
      >
        {
          (hasPermission: boolean) => (
            <SideBar
              heading={t('external_storage', 'External Storage')}
              subheading={data ? `(${data.length})` : ''}
              button={
                hasPermission ? (
                  <Link
                    as={NavLink}
                    position="relative"
                    display="flex"
                    alignItems="center"
                    to={`${path}/new`}
                    _hover={{ textDecoration: 'none', outline: 'none' }}
                  >
                    <Button>+{t('add_new', 'Add New')}</Button>
                  </Link>
                ) : <></>
              }
            >
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                padding="1.5em 0.75em"
              />
              {!loading ? (
                data?.length ? (
                  <Box
                    overflow="scroll"
                    h="100%"
                    position="relative"
                    padding="0em 0.75em 1.5em"
                  >
                    <Shadow backgroundColor={backgroundColor} />
                    <VStack spacing="1em" divider={<Divider />}>
                      {filteredData.length ? (
                        filteredData.map(x => {
                          const sourceMetaData = extractMetadata(x)

                          return (
                            <SideBarItem to={`${path}/${encodeHash(x.id)}`} key={x.id}>
                              <Avatar
                                size="md"
                                margin="1em 0.5em"
                                background="transparent"
                                padding={1}
                                borderRadius="0px"
                              >
                                <Image
                                  src={getOriginIconUrl(x)}
                                  fallbackSrc="https://ik.imagekit.io/demo/img/icons8-server-64_sibZOIGTb.png"
                                />
                              </Avatar>
                              <Flex
                                flexDirection="column"
                                width="100%"
                                padding={2}
                                justifyContent="center"
                                overflow="hidden"
                              >
                                <Tooltip label={sourceMetaData.sourceName}>
                                  <ChakraText
                                    fontSize="md"
                                    fontWeight="medium"
                                    color={colors.appDarkIndigo}
                                    lineHeight="base"
                                    overflow="hidden"
                                    isTruncated
                                  >
                                    {sourceMetaData.sourceName}
                                  </ChakraText>
                                </Tooltip>

                                <ChakraText
                                  fontSize="md"
                                  fontWeight="normal"
                                  color={colors.appBlue}
                                  lineHeight="base"
                                  overflow="hidden"
                                  isTruncated
                                >
                                  {sourceTypeMapping[x.typeId].name}
                                </ChakraText>
                                {sourceMetaData.sourceDescription !== '' ? (
                                  <Tooltip label={sourceMetaData.sourceDescription}>
                                    <ChakraText
                                      fontSize="md"
                                      fontWeight="normal"
                                      color={colors.appSlate}
                                      lineHeight="base"
                                      overflow="hidden"
                                      isTruncated
                                    >
                                      {sourceMetaData.sourceDescription}
                                    </ChakraText>
                                  </Tooltip>
                                ) : null}
                              </Flex>
                            </SideBarItem>
                          )
                        })
                      ) : (
                        <ChakraText color="appBattleshipGrey">
                          {t('no_matches_found', 'No matches found')}
                        </ChakraText>
                      )}
                    </VStack>
                  </Box>
                ) : (
                  <Flex
                    flexDirection="column"
                    textAlign="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                  >
                    <ExternalStorageGraphic
                      height="101px"
                      width="101px"
                      margin="0 auto"
                      marginBottom="1em"
                    />
                    <ChakraText
                      color={colors.appBattleshipGrey}
                      fontWeight="500"
                      fontSize="1em"
                      margin="0 auto"
                    >
                      {t('no_external_origin_added', 'No External Storage added.')}

                      <br />
                      {t(
                        'add_origin_to_show_list',
                        'Please add new origin to show list',
                      )}
                    </ChakraText>
                  </Flex>
                )
              ) : (
                <Center height="100%">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="appPaleGrey"
                    color="appBlue"
                    size="xl"
                  />
                </Center>
              )}
            </SideBar>
          )
        }
      </PermissionGuard>
    )
  },
)

export default ExternalStorageSideMenu

export interface ExternalStorageSideMenuRef {
  getSources(): Promise<void>
}
